import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, Pill, Leaf, Microscope, Stethoscope } from 'lucide-react';
import { productService } from '@/services/product.service';
import api from '@/services/api';
import { Product, Banner, Category } from '@/types';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductCardSkeleton } from '@/components/common/Skeleton';
import { FlashDealsSection } from '@/components/common/FlashDealsSection';
import { PharmacistNote } from '@/components/common/PharmacistNote';

export const HomePage = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [featured, setFeatured] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/banners'),
      productService.getFeatured(),
      api.get('/categories'),
    ]).then(([bRes, fRes, cRes]) => {
      setBanners(bRes.data.data || []);
      setFeatured(fRes.data.data || []);
      setCategories((cRes.data.data || []).slice(0, 8));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setCurrentBanner(p => (p + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  const banner = banners[currentBanner];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="min-h-screen bg-cream">

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-primary-600" style={{ minHeight: 440 }}>
        {banner ? (
          <>
            <img src={banner.imageUrl} alt={banner.title} className="w-full h-full object-cover absolute inset-0 opacity-30" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
              <p className="kicker text-white/60 mb-4">{greeting}, Nadiad</p>
              <h1 className="font-display italic text-white text-4xl md:text-6xl font-light leading-[1.06] max-w-xl mb-6 drop-shadow-sm">
                {banner.title}
              </h1>
              {banner.linkUrl && (
                <Link to={banner.linkUrl} className="inline-flex items-center gap-2 bg-white text-primary-600 font-semibold px-6 py-3 rounded-pill shadow-warm hover:bg-cream transition-all">
                  Shop now
                </Link>
              )}
            </div>
            {banners.length > 1 && (
              <>
                <button onClick={() => setCurrentBanner(p => (p - 1 + banners.length) % banners.length)} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white transition-all backdrop-blur-sm">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => setCurrentBanner(p => (p + 1) % banners.length)} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white transition-all backdrop-blur-sm">
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {banners.map((_, i) => (
                    <button key={i} onClick={() => setCurrentBanner(i)} className={`h-1 rounded-full transition-all duration-300 ${i === currentBanner ? 'bg-white w-6' : 'bg-white/40 w-2'}`} />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
            <p className="kicker text-white/60 mb-4">{greeting}, Nadiad</p>
            <h1 className="font-display italic text-white text-4xl md:text-6xl font-light leading-[1.06] max-w-xl mb-6">
              Your health,<br />our care.
            </h1>
            <p className="text-white/70 text-base max-w-md mb-8">Quality medicines, wellness products and expert guidance — all from a pharmacist who knows your name.</p>
            <Link to="/categories/medicines" className="inline-flex items-center gap-2 bg-white text-primary-600 font-semibold px-6 py-3 rounded-pill shadow-warm hover:bg-cream transition-all">
              Browse medicines
            </Link>
          </div>
        )}
      </section>

      {/* ── Trust strip ──────────────────────────────────── */}
      <section className="bg-cream-2 border-b border-rule">
        <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {TRUST_ITEMS.map(({ icon: Icon, text, sub }) => (
            <div key={text} className="flex items-center gap-3 py-1">
              <div className="w-9 h-9 rounded-md bg-primary-50 flex items-center justify-center shrink-0">
                <Icon className="h-4.5 w-4.5 text-primary-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-ink leading-tight">{text}</p>
                <p className="text-xs text-muted leading-tight">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Flash Deals ──────────────────────────────────── */}
      <FlashDealsSection />

      {/* ── Quick action tiles ───────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {ACTION_TILES.map(tile => (
            <Link
              key={tile.label}
              to={tile.to}
              className="group flex flex-col items-center gap-3 p-5 bg-cream-2 rounded-card border border-rule/60 shadow-soft hover:shadow-card hover:border-primary-200 transition-all duration-300 text-center"
            >
              <div className="text-3xl">{tile.emoji}</div>
              <div>
                <p className="text-sm font-semibold text-ink group-hover:text-primary-600 transition-colors">{tile.label}</p>
                <p className="text-xs text-muted mt-0.5 leading-tight">{tile.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── Shop by category — shelf rows ────────────────── */}
      <section className="max-w-7xl mx-auto px-4 pb-10">
        <div className="flex items-end justify-between mb-6">
          <h2 className="section-title">Shop the Shelves</h2>
          <Link to="/categories/medicines" className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors">View all →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {categories.map(cat => (
            <Link
              key={cat.id}
              to={`/categories/${cat.slug}`}
              className="group flex flex-col items-center p-3.5 bg-cream-2 rounded-card border border-rule/50 shadow-soft hover:shadow-card hover:border-primary-200/80 hover:-translate-y-0.5 transition-all duration-300 text-center"
            >
              <div className="text-3xl mb-2">{cat.icon || '💊'}</div>
              <p className="text-xs font-medium text-ink group-hover:text-primary-600 transition-colors leading-tight">{cat.name}</p>
            </Link>
          ))}
        </div>
        {/* Shelf plank decorative element */}
        <div className="shelf-plank mt-1 rounded-b-md" />
      </section>

      {/* ── Featured products ────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 pb-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="section-title">Staff Picks</h2>
          <Link to="/categories/medicines" className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors">See all →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {loading
            ? Array(10).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)
            : featured.slice(0, 10).map(p => <ProductCard key={p.id} product={p} />)
          }
        </div>
        <div className="shelf-plank mt-1 rounded-b-md" />
      </section>

      {/* ── Pharmacist note ──────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-4 pb-14">
        <PharmacistNote
          note="This monsoon season, keep your immunity up. Vitamin C, zinc, and a good probiotic go a long way. And remember — if something doesn't feel right, don't wait. Come in and we'll sort it together."
          name="Dr. Priya Shah"
          title="Head Pharmacist"
          month="May 2025"
        />
      </section>

      {/* ── Milan Circle loyalty ──────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 pb-14">
        <div className="relative overflow-hidden bg-primary-500 rounded-card p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 text-white">
          <div className="text-5xl">🍃</div>
          <div className="flex-1 text-center md:text-left">
            <p className="kicker text-white/60 mb-2">Milan Circle</p>
            <h3 className="font-display text-2xl font-semibold mb-2">Earn leaves. Unlock care.</h3>
            <p className="text-white/80 text-sm max-w-md">1 leaf for every ₹10 spent. 100 leaves = ₹10 off your next order. Graduate from Friend → Family → Inner Circle.</p>
          </div>
          <Link to="/register" className="bg-white text-primary-600 font-semibold px-7 py-3 rounded-pill hover:bg-cream transition-all shrink-0 shadow-soft">
            Join free
          </Link>
        </div>
      </section>

      {/* ── Ayurvedic shelf ──────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="section-title">🌱 Ayurvedic & Herbal</h2>
          <Link to="/categories/ayurvedic-herbal" className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors">See all →</Link>
        </div>
        <AyurvedicShelf />
        <div className="shelf-plank mt-1 rounded-b-md" />
      </section>
    </div>
  );
};

const AyurvedicShelf = () => {
  const [products, setProducts] = useState<Product[]>([]);
  useEffect(() => {
    productService.getProducts({ category: 'ayurvedic-herbal', limit: 5, page: 1 })
      .then(res => setProducts(res.data.data || []));
  }, []);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {products.map(p => <ProductCard key={p.id} product={p} />)}
    </div>
  );
};

const TRUST_ITEMS = [
  { icon: Stethoscope, text: 'Licensed pharmacy',        sub: 'Since 1987 · Nadiad' },
  { icon: Leaf,        text: '100% genuine products',    sub: 'Direct from distributors' },
  { icon: Microscope,  text: 'Expert pharmacists',       sub: '12 specialists on team' },
  { icon: Pill,        text: 'Free delivery above ₹500', sub: 'Nadiad & nearby areas' },
];

const ACTION_TILES = [
  { emoji: '📋', label: 'Upload Prescription', sub: 'Verified within 2 hrs', to: '/prescriptions' },
  { emoji: '🩺', label: 'Find by Symptom',     sub: "What's troubling you?",  to: '/symptoms' },
  { emoji: '🔍', label: 'Drug Checker',         sub: 'Check interactions',    to: '/drug-checker' },
  { emoji: '📄', label: 'Expense Vault',        sub: 'Track health spending', to: '/expenses' },
];
